import React from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowRight, TrendingUp, DollarSign, Leaf, Zap, Activity, Shield, CloudOff, Calculator,
  Smartphone, Monitor, MessageSquare, BarChart3, Bell, Gauge, Cpu,
  Radio, Brain, Workflow, Lock, Globe, Users, Wrench, Play, Target
} from 'lucide-react';
import Button from '../../components/ui/Button';
import MetricCard from '../../components/ui/MetricCard';
import GlassPanel from '../../components/ui/GlassPanel';
import { Link } from 'react-router-dom';

const LandingPage = () => {
  // Smooth scroll handler
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-background-dark">
      {/* Hero Section */}
      <section id="home" className="relative min-h-[85vh] flex items-center justify-center pt-8 sm:pt-12">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-background-dark z-10"></div>
          <div
            className="w-full h-full bg-cover bg-center"
            style={{
              backgroundImage: `url("https://lh3.googleusercontent.com/aida-public/AB6AXuD4gQ9fqyq78URnIkKEbCS-kzoO0bfx84DetNCSQuZFumSNbuwJaT9FPhqlHkBP2Bgubv_AEElZGUxDbMU7TW0K8d5ZmLeHUWRd3Y2qg1M2TrtO_QEebVVPP3b5ZKG14JUxv_U_K4kqSCG_J3wHLAwTw5p0j5gOVChYaP7Ijd8ZV8P9yfumrmxmkkUhdP-L3Aax8oZ-Xg2x-_qsQ3_1wSe9BVvrmzHvVVZXGTIylWixNH0wIipsHRXuXy3d35FCIks-W__TyQgeWQ4")`
            }}
          ></div>
        </div>

        <div className="relative z-10 w-full max-w-[1280px] px-6 py-12 sm:py-16 flex flex-col items-start gap-8">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm w-fit"
          >
            <Zap className="w-4 h-4 text-primary" />
            <span className="text-xs font-semibold tracking-wide uppercase text-white/90">
              Autonomous Plunger Lift Control
            </span>
          </motion.div>

          {/* Hero Text */}
          <div className="max-w-4xl">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black leading-[1.1] tracking-tight mb-6 text-white drop-shadow-lg"
            >
              <span className="sm:whitespace-nowrap">Your Wells Run Autonomously.</span><br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-orange-300 sm:whitespace-nowrap">
                Production Up. OpEx Down.
              </span>
              <br />
              <span className="sm:whitespace-nowrap">No Human in the Loop.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-base sm:text-lg md:text-xl text-gray-200 font-light max-w-2xl leading-relaxed mb-6 sm:mb-8"
            >
              PlungerLift AI writes live setpoints to your SCADA - autonomously optimizing 
              every shutin and afterflow cycle, per well, around the clock. 
              No monitoring. No field visits. No guesswork.
            </motion.p>
          </div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto"
          >
            <Link to="/schedule-a-demo">
              <Button size="lg" icon={ArrowRight} className="w-full sm:w-auto shadow-[0_0_20px_rgba(230,126,34,0.3)]">
                Request Production Assessment
              </Button>
            </Link>
            <button 
              onClick={() => scrollToSection('technology')}
              className="flex items-center justify-center rounded-lg px-6 bg-white/10 hover:bg-white/20 transition-all text-white text-sm font-bold tracking-wide border border-white/20"
            >
              See the Economics
            </button>
          </motion.div>
        </div>
      </section>

      {/* Metric Ribbon */}
      <section className="relative z-20 -mt-20 md:-mt-24 px-6 pb-20">
        <div className="max-w-[1280px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-1 bg-slate-dark/90 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
            <MetricCard
              icon={TrendingUp}
              value="10"
              suffix="%"
              label="Production Uplift Per Well"
              index={0}
            />
            <MetricCard
              icon={DollarSign}
              value="65"
              suffix="%"
              label="Fewer Field Visits"
              index={1}
            />
            <MetricCard
              icon={Leaf}
              value="80"
              suffix="%"
              label="Venting Reduction"
              color="green-400"
              index={2}
            />
          </div>
        </div>
      </section>

      {/* Core Problems We Solve Section */}
      <section id="technology" className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 bg-[#211811]">
        <div className="max-w-[1280px] mx-auto flex flex-col gap-10 sm:gap-12 lg:gap-16">
          {/* Section Header */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-white/10 pb-8">
            <div className="max-w-2xl">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-primary text-sm font-bold uppercase tracking-widest mb-3"
              >
                The Economics
              </motion.h2>
              <motion.h3
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-3xl md:text-4xl font-bold text-white font-heading"
              >
                Manual Control Doesn't Scale.
              </motion.h3>
            </div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-gray-400 max-w-md text-sm leading-relaxed"
            >
              Every plunger well requires <span className="text-white font-semibold">continuous cycle optimization</span>—yet 
              operators manage hundreds of wells with static timers and field visits. The result: 
              <span className="text-red-400 font-semibold"> lost production, excess venting, and rising OpEx</span>.
            </motion.p>
          </div>

          
          {/* Stats Banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-xl sm:rounded-2xl bg-gradient-to-r from-surface-dark via-slate-900 to-surface-dark border border-white/10 overflow-hidden"
          >
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5">
              <div className="text-center p-4 sm:p-6 bg-surface-dark">
                <p className="text-2xl sm:text-3xl md:text-4xl font-black text-primary">~1M</p>
                <p className="text-[10px] sm:text-xs text-gray-400 mt-1 sm:mt-2">Plunger Wells in U.S.</p>
              </div>
              <div className="text-center p-4 sm:p-6 bg-surface-dark">
                <p className="text-2xl sm:text-3xl md:text-4xl font-black text-green-400">$2B+</p>
                <p className="text-[10px] sm:text-xs text-gray-400 mt-1 sm:mt-2">Annual Value Lost to Venting</p>
              </div>
              <div className="text-center p-4 sm:p-6 bg-surface-dark">
                <p className="text-2xl sm:text-3xl md:text-4xl font-black text-primary">15-20%</p>
                <p className="text-[10px] sm:text-xs text-gray-400 mt-1 sm:mt-2">Typical Non-Arrival Rate</p>
              </div>
              <div className="text-center p-4 sm:p-6 bg-surface-dark">
                <p className="text-2xl sm:text-3xl md:text-4xl font-black text-white">24/7</p>
                <p className="text-[10px] sm:text-xs text-gray-400 mt-1 sm:mt-2">Monitoring Required</p>
              </div>
            </div>
            {/* References */}
            <div className="px-6 py-4 bg-black/40 border-t border-white/10">
              <p className="text-xs text-gray-400 text-center leading-relaxed">
                <span className="text-gray-500">Sources:</span> EPA Methane Inventory &bull; PTTC Plunger Lift Studies &bull; Operator Field Data
              </p>
            </div>
          </motion.div>

         {/* Industry Context */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative rounded-xl sm:rounded-2xl bg-gradient-to-br from-white/5 to-transparent border border-white/10 p-6 sm:p-8 lg:p-10"
          >
            {/* Icon badge */}
            <div className="absolute -top-4 left-6 sm:left-8 lg:left-10">
              <div className="w-8 h-8 rounded-lg bg-primary/20 border border-primary/30 flex items-center justify-center">
                <Activity className="w-4 h-4 text-primary" />
              </div>
            </div>
            
            <div className="space-y-4 pt-2">
              <p className="text-base sm:text-lg md:text-xl leading-relaxed text-gray-300">
                Across thousands of gas wells, plunger lift performance is still governed by 
                <span className="text-white font-medium"> static rules</span>, 
                <span className="text-white font-medium"> delayed human decisions</span>, and 
                <span className="text-white font-medium"> incomplete data</span>.
              </p>
              
              <p className="text-base sm:text-lg md:text-xl leading-relaxed text-gray-400">
                Small inefficiencies in cycle timing compound into 
                <span className="text-red-400"> lost production</span>, 
                <span className="text-red-400"> excess venting</span>, 
                <span className="text-red-400"> equipment wear</span>, and 
                <span className="text-red-400"> rising operating costs</span> at fleet scale.
              </p>
              
              <div className="flex items-start gap-3 pt-4 border-t border-white/10">
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-1">
                  <Zap className="w-3 h-3 text-primary" />
                </div>
                <p className="text-base sm:text-lg md:text-xl leading-relaxed text-gray-300">
                  The opportunity is not incremental optimization—it is 
                  <span className="text-primary font-semibold"> full autonomy</span>: systems that learn from every cycle, 
                  act in real time, and continuously improve well performance 
                  <span className="text-white font-medium"> without human intervention</span>.
                </p>
              </div>
            </div>
          </motion.div>




          {/* Risk Cards - Sorted Highest to Lowest */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-5">
            {/* Highest Risk - Red */}
            <GlassPanel className="bg-gradient-to-b from-red-950/40 to-transparent hover:border-red-500/50 border-red-500/30">
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div className="size-10 rounded-lg bg-red-500/20 border border-red-500/30 flex items-center justify-center text-red-400">
                    <CloudOff className="w-5 h-5" />
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-red-500/20 text-red-400 border border-red-500/30">
                    Critical
                  </span>
                </div>
                <div>
                  <h4 className="text-base font-bold text-white mb-2 font-heading">
                    Revenue Lost to Atmosphere
                  </h4>
                  <p className="text-gray-400 leading-relaxed text-xs">
                    Every blowdown is <span className="text-red-400">gas you paid to produce</span>—
                    vented instead of sold. Plus <span className="text-red-400">EPA scrutiny</span> and 
                    <span className="text-red-400"> ESG liability</span>.
                  </p>
                </div>
              </div>
            </GlassPanel>

            {/* High Risk - Orange */}
            <GlassPanel className="bg-gradient-to-b from-orange-950/40 to-transparent hover:border-orange-500/50 border-orange-500/30">
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div className="size-10 rounded-lg bg-orange-500/20 border border-orange-500/30 flex items-center justify-center text-orange-400">
                    <Zap className="w-5 h-5" />
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-orange-500/20 text-orange-400 border border-orange-500/30">
                    High
                  </span>
                </div>
                <div>
                  <h4 className="text-base font-bold text-white mb-2 font-heading">
                    Equipment Failures
                  </h4>
                  <p className="text-gray-400 leading-relaxed text-xs">
                    <span className="text-orange-400">Hard arrivals</span> destroy 
                    lubricators, bumper springs, and catchers. 
                    <span className="text-orange-400">Unplanned CapEx</span> every quarter.
                  </p>
                </div>
              </div>
            </GlassPanel>

            {/* Medium-High Risk - Amber */}
            <GlassPanel className="bg-gradient-to-b from-amber-950/40 to-transparent hover:border-amber-500/50 border-amber-500/30">
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div className="size-10 rounded-lg bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400">
                    <Activity className="w-5 h-5" />
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
                    High
                  </span>
                </div>
                <div>
                  <h4 className="text-base font-bold text-white mb-2 font-heading">
                    Unplanned Downtime
                  </h4>
                  <p className="text-gray-400 leading-relaxed text-xs">
                    Non-arrivals and liquid loading = 
                    <span className="text-amber-400"> wells offline</span>. 
                    Manual revival takes hours of 
                    <span className="text-amber-400"> production loss</span>.
                  </p>
                </div>
              </div>
            </GlassPanel>

            {/* Medium Risk - Yellow */}
            <GlassPanel className="bg-gradient-to-b from-yellow-950/40 to-transparent hover:border-yellow-500/50 border-yellow-500/30">
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div className="size-10 rounded-lg bg-yellow-500/20 border border-yellow-500/30 flex items-center justify-center text-yellow-400">
                    <Gauge className="w-5 h-5" />
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                    Medium
                  </span>
                </div>
                <div>
                  <h4 className="text-base font-bold text-white mb-2 font-heading">
                    Suboptimal Cycles
                  </h4>
                  <p className="text-gray-400 leading-relaxed text-xs">
                    Static timers ignore changing conditions. 
                    <span className="text-yellow-400">Every mistuned cycle</span> leaves 
                    production on the table.
                  </p>
                </div>
              </div>
            </GlassPanel>

            {/* Operational Burden - Cyan */}
            <GlassPanel className="bg-gradient-to-b from-cyan-950/40 to-transparent hover:border-cyan-500/50 border-cyan-500/30">
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div className="size-10 rounded-lg bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center text-cyan-400">
                    <Wrench className="w-5 h-5" />
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                    Ongoing
                  </span>
                </div>
                <div>
                  <h4 className="text-base font-bold text-white mb-2 font-heading">
                    Unsustainable OpEx
                  </h4>
                  <p className="text-gray-400 leading-relaxed text-xs">
                    <span className="text-cyan-400">Field visits</span>, 
                    <span className="text-cyan-400"> pumper time</span>, 
                    <span className="text-cyan-400"> trucks</span>—all to adjust timers 
                    that need changing again tomorrow.
                  </p>
                </div>
              </div>
            </GlassPanel>
          </div>
        </div>
      </section>

      {/* AI Solution Section */}
      <section id="platform" className="py-12 sm:py-16 lg:py-24 px-4 sm:px-6 bg-background-dark relative overflow-hidden">
        {/* Animated background grid */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(rgba(230,126,34,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(230,126,34,0.3) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>
        
        <div className="max-w-[1280px] mx-auto relative z-10">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/30 mb-6"
            >
              <Brain className="w-5 h-5 text-primary" />
              <span className="text-sm font-semibold text-primary">Autonomous Control</span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4 sm:mb-6"
            >
              The System Decides.{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-orange-300">
                The Well Executes.
              </span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto px-4 sm:px-0"
            >
              PlungerLift AI calculates optimal setpoints for each well, each cycle—and writes them 
              directly to your SCADA. No alerts to acknowledge. No recommendations to review. 
              Fully autonomous.
            </motion.p>
          </div>

          {/* Platform Demo GIF */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="relative"
          >
            {/* Glow behind image */}
            <div className="absolute inset-0 bg-primary/20 blur-[100px] rounded-full"></div>
            
            {/* GIF Container */}
            <div className="relative rounded-2xl border border-white/10 p-2 shadow-2xl overflow-hidden">
              <img
                src="/optm.gif"
                alt="PlungerXcel Platform Demo"
                className="w-full rounded-xl"
              />
            </div>
          </motion.div>

          {/* AI Dashboard Mockup - Commented out for now */}
          {/*
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="relative"
          >
            <div className="absolute inset-0 bg-primary/20 blur-[100px] rounded-full"></div>
            <div className="relative bg-surface-dark/80 backdrop-blur-xl rounded-2xl border border-white/10 p-6 shadow-2xl">
              <div className="flex items-center gap-2 mb-6 pb-4 border-b border-white/10">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500/80"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500/80"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500/80"></div>
                </div>
                <div className="flex-1 flex justify-center">
                  <div className="px-4 py-1.5 rounded-full bg-black/30 text-xs text-gray-400 flex items-center gap-2">
                    <Lock className="w-3 h-3" />
                    app.plungerxcel.com/dashboard
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-2 space-y-3">
                  {[
                    { icon: Gauge, label: 'Wells', active: true },
                    { icon: BarChart3, label: 'Cycles' },
                    { icon: Bell, label: 'Alerts' },
                    { icon: MessageSquare, label: 'AI Chat' },
                  ].map((item, i) => (
                    <motion.div
                      key={item.label}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.4 + i * 0.1 }}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                        item.active 
                          ? 'bg-primary/20 text-primary border border-primary/30' 
                          : 'text-gray-500 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <item.icon className="w-4 h-4" />
                      <span className="hidden lg:block">{item.label}</span>
                    </motion.div>
                  ))}
                </div>
                <div className="col-span-10 space-y-4">
                  <div className="grid grid-cols-4 gap-3">
                    {[
                      { label: 'Active Wells', value: '47', change: '+3' },
                      { label: 'Cycles Today', value: '284', change: 'Normal' },
                      { label: 'Gas Output', value: '892 MCF', change: '+18%' },
                      { label: 'AI Adjustments', value: '156', change: 'Live' },
                    ].map((stat, i) => (
                      <motion.div
                        key={stat.label}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.5 + i * 0.1 }}
                        className="bg-black/30 rounded-xl p-4 border border-white/5"
                      >
                        <p className="text-xs text-gray-500 mb-1">{stat.label}</p>
                        <div className="flex items-end justify-between">
                          <span className="text-xl font-bold text-white">{stat.value}</span>
                          <span className="text-xs text-green-400">{stat.change}</span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.8 }}
                    className="bg-black/30 rounded-xl p-4 border border-white/5 h-48"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm text-white font-semibold">Well Performance</span>
                      <div className="flex items-center gap-2 text-xs text-green-400">
                        <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
                        Live
                      </div>
                    </div>
                    <div className="relative h-28 flex items-end">
                      <svg className="w-full h-full" viewBox="0 0 400 100" preserveAspectRatio="none">
                        <motion.path
                          initial={{ pathLength: 0 }}
                          whileInView={{ pathLength: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 2, delay: 1 }}
                          d="M0,80 Q50,60 100,65 T200,40 T300,55 T400,30"
                          fill="none"
                          stroke="url(#gradient)"
                          strokeWidth="2"
                        />
                        <defs>
                          <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#E67E22" />
                            <stop offset="100%" stopColor="#F39C12" />
                          </linearGradient>
                        </defs>
                      </svg>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
          */}

          {/* AI Features Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mt-8 sm:mt-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="glass-panel p-6 rounded-xl border border-white/10 hover:border-primary/30 transition-colors group"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center text-primary mb-4 group-hover:bg-primary group-hover:text-white transition-colors">
                <Workflow className="w-6 h-6" />
              </div>
              <h4 className="text-lg font-bold text-white mb-2">Continuous Optimization</h4>
              <p className="text-sm text-gray-400 leading-relaxed mb-3">
                <span className="text-white">The next setpoint is calculated before the current cycle ends.</span> Every well, every hour, around the clock.
              </p>
              <ul className="text-xs text-gray-500 space-y-2">
                {[
                  '• Shutin & afterflow tuned to live well conditions',
                  '• Learns from every cycle across your fleet',
                  '• Writes setpoints directly—no human approval needed'
                ].map((item, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 + i * 0.1 }}
                    className="text-gray-400 group-hover:text-gray-300 transition-colors"
                  >
                    {item}
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="glass-panel p-6 rounded-xl border border-white/10 hover:border-primary/30 transition-colors group"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center text-primary mb-4 group-hover:bg-primary group-hover:text-white transition-colors">
                <Radio className="w-6 h-6" />
              </div>
              <h4 className="text-lg font-bold text-white mb-2">Direct SCADA Integration</h4>
              <p className="text-sm text-gray-400 leading-relaxed mb-3">
                <span className="text-white">Connects to your existing infrastructure.</span> No new hardware. No rip-and-replace. Live in weeks.
              </p>
              <ul className="text-xs text-gray-500 space-y-1">
                <li>• MQTT, Modbus, OPC-UA protocols</li>
                <li>• Works with all major RTU/PLC vendors</li>
                <li>• Secure cloud or on-prem deployment</li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="glass-panel p-6 rounded-xl border border-white/10 hover:border-primary/30 transition-colors group"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center text-primary mb-4 group-hover:bg-primary group-hover:text-white transition-colors">
                <Shield className="w-6 h-6" />
              </div>
              <h4 className="text-lg font-bold text-white mb-2">Autonomous Recovery</h4>
              <p className="text-sm text-gray-400 leading-relaxed mb-3">
                <span className="text-white">Detects problems before they escalate.</span> Executes recovery sequences automatically—no phone calls needed.
              </p>
              <ul className="text-xs text-gray-500 space-y-1">
                <li>• Identifies liquid loading, stuck plungers, compressor issues</li>
                <li>• Initiates revival protocols autonomously</li>
                <li>• 70% faster return to production</li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="glass-panel p-6 rounded-xl border border-white/10 hover:border-primary/30 transition-colors group"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center text-primary mb-4 group-hover:bg-primary group-hover:text-white transition-colors">
                <Users className="w-6 h-6" />
              </div>
              <h4 className="text-lg font-bold text-white mb-2">Zero Field Dependency</h4>
              <p className="text-sm text-gray-400 leading-relaxed mb-3">
                <span className="text-white">No staff required on location. No screens to watch.</span> The system operates independently—we provide 24/7 support so you don't have to.
              </p>
              <ul className="text-xs text-gray-500 space-y-1">
                <li>• Fully autonomous—no manual intervention needed</li>
                <li>• Our team monitors system health around the clock</li>
                <li>• You focus on strategy, not setpoints</li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="glass-panel p-6 rounded-xl border border-white/10 hover:border-primary/30 transition-colors group"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center text-primary mb-4 group-hover:bg-primary group-hover:text-white transition-colors">
                <Brain className="w-6 h-6" />
              </div>
              <h4 className="text-lg font-bold text-white mb-2">Per-Well Intelligence</h4>
              <p className="text-sm text-gray-400 leading-relaxed mb-3">
                <span className="text-white">Each well gets its own adaptive model.</span> Performance improves over time as the system learns your well's unique behavior.
              </p>
              <ul className="text-xs text-gray-500 space-y-1">
                <li>• Custom neural network per well—not one-size-fits-all</li>
                <li>• Continuously refines predictions with each cycle</li>
                <li>• Gets smarter the longer it runs</li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.7 }}
              className="glass-panel p-6 rounded-xl border border-white/10 hover:border-primary/30 transition-colors group"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center text-primary mb-4 group-hover:bg-primary group-hover:text-white transition-colors">
                <Zap className="w-6 h-6" />
              </div>
              <h4 className="text-lg font-bold text-white mb-2">Live in 5–7 Days</h4>
              <p className="text-sm text-gray-400 leading-relaxed mb-3">
                <span className="text-white">From data connection to autonomous control in under a week.</span> No lengthy pilots. No months of tuning. Results start immediately.
              </p>
              <ul className="text-xs text-gray-500 space-y-1">
                <li>• Single-well onboarding in 5–7 days</li>
                <li>• Fleet rollout scales linearly</li>
                <li>• Value delivered from day one</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section id="results" className="py-12 sm:py-16 lg:py-24 px-4 sm:px-6 bg-gradient-to-br from-surface-dark to-black relative overflow-hidden">
        {/* Abstract Background Element */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-primary/5 blur-[120px] rounded-full pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-1/3 h-1/2 bg-forest/5 blur-[100px] rounded-full pointer-events-none"></div>

        <div className="max-w-[1280px] mx-auto relative z-10">
          {/* Top Content - Full Width */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-forest/20 text-green-400 text-xs font-bold uppercase tracking-wider mb-6 border border-forest/40">
              <Target className="w-4 h-4" />
              Deployed Results
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6 font-heading leading-tight">
              Measured Across{' '}
              <span className="text-primary">Operating Wells.</span>
            </h2>
            <p className="text-gray-300 text-base sm:text-lg leading-relaxed max-w-3xl mx-auto px-4 sm:px-0">
              These aren't projections—they're outcomes observed across deployed wells within weeks of activation. 
              Consistent. Repeatable. Verifiable against your production data.
            </p>
          </motion.div>

          {/* Stacked Images Container - Full Width */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="relative w-full max-w-6xl mx-auto mb-8 md:mb-16"
          >
            {/* Mobile: Stacked vertically, Desktop: Overlapping */}
            <div className="flex flex-col gap-6 md:hidden">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
              >
                <div className="rounded-xl p-2 border border-white/10 shadow-xl">
                  <img
                    src="/production_plot.png"
                    alt="Production Comparison - Before and After AI"
                    className="w-full rounded-lg"
                  />
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
              >
                <div className="rounded-xl p-2 border border-primary/20 shadow-xl">
                  <img
                    src="/prod_inc.png"
                    alt="Production Increase Analysis"
                    className="w-full rounded-lg"
                  />
                </div>
              </motion.div>
            </div>

            {/* Desktop: Overlapping layout */}
            <div className="hidden md:block relative" style={{ minHeight: '600px' }}>
              {/* Back Image - Production Increase (Top Left) */}
              <motion.div
                initial={{ opacity: 0, x: -30, y: -30 }}
                whileInView={{ opacity: 1, x: 0, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="absolute -left-4 lg:-left-8 -top-4 lg:-top-8 w-[85%] lg:w-[80%] z-20"
              >
                <div className="rounded-2xl p-3 border border-white/10 shadow-2xl">
                    <img
                      src="/production_plot.png"
                      alt="Production Comparison - Before and After AI"
                      className="w-full rounded-lg"
                    />
                  </div>
              </motion.div>

              {/* Front Image - Production Plot (Bottom Right, Elevated) */}
              <motion.div
                initial={{ opacity: 0, x: 30, y: 30 }}
                whileInView={{ opacity: 1, x: 0, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
                className="absolute -right-4 lg:-right-16 -bottom-12 lg:-bottom-20 w-[85%] lg:w-[80%] z-10"
              >
                <div className="rounded-2xl p-3 border border-primary/20 shadow-[0_25px_80px_-12px_rgba(255,107,0,0.25)]">
                    <img
                      src="/prod_inc.png"
                      alt="Production Increase Analysis"
                      className="w-full rounded-lg"
                    />
                  </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Benefits Grid - Below Images */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mt-8 md:mt-24 lg:mt-32">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ y: -12, scale: 1.03, transition: { type: 'spring', stiffness: 400, damping: 10 } }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.5 }}
              className="bg-surface-dark/50 backdrop-blur-sm rounded-xl p-6 border border-white/5 text-center hover:border-blue-500/50 transition-all group cursor-pointer shadow-lg hover:shadow-blue-500/30"
            >
              <motion.div 
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="size-12 rounded-full bg-blue-500/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-500/30 transition-all group-hover:scale-110"
              >
                <Cpu className="w-6 h-6 text-blue-400 group-hover:text-blue-300 transition-colors" />
              </motion.div>
              <motion.p 
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.25 }}
                className="text-white font-semibold mb-2 group-hover:text-blue-300 transition-colors"
              >
                Autonomous Operation
              </motion.p>
              <motion.p 
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="text-gray-500 text-sm group-hover:text-gray-400 transition-colors"
              >
                Every decision made by the system—not suggested
              </motion.p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ y: -12, scale: 1.03, transition: { type: 'spring', stiffness: 400, damping: 10 } }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="bg-surface-dark/50 backdrop-blur-sm rounded-xl p-6 border border-white/5 text-center hover:border-primary/50 transition-all group cursor-pointer shadow-lg hover:shadow-primary/30"
            >
              <motion.div 
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="size-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/30 transition-all group-hover:scale-110"
              >
                <Target className="w-6 h-6 text-primary group-hover:text-orange-200 transition-colors" />
              </motion.div>
              <motion.p 
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.35 }}
                className="text-white font-semibold mb-2 group-hover:text-orange-300 transition-colors"
              >
                80% Less Venting
              </motion.p>
              <motion.p 
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="text-gray-500 text-sm group-hover:text-gray-400 transition-colors"
              >
                Precise afterflow timing captures gas that would vent
              </motion.p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ y: -12, scale: 1.03, transition: { type: 'spring', stiffness: 400, damping: 10 } }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="bg-surface-dark/50 backdrop-blur-sm rounded-xl p-6 border border-white/5 text-center hover:border-green-500/50 transition-all group cursor-pointer shadow-lg hover:shadow-green-500/30"
            >
              <motion.div 
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="size-12 rounded-full bg-forest/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-forest/30 transition-all group-hover:scale-110"
              >
                <Gauge className="w-6 h-6 text-green-400 group-hover:text-green-300 transition-colors" />
              </motion.div>
              <motion.p 
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.45 }}
                className="text-white font-semibold mb-2 group-hover:text-green-300 transition-colors"
              >
                65% Fewer Site Visits
              </motion.p>
              <motion.p 
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
                className="text-gray-500 text-sm group-hover:text-gray-400 transition-colors"
              >
                Pumpers focus on exceptions, not routine adjustments
              </motion.p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ y: -12, scale: 1.03, transition: { type: 'spring', stiffness: 400, damping: 10 } }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="bg-surface-dark/50 backdrop-blur-sm rounded-xl p-6 border border-white/5 text-center hover:border-purple-500/50 transition-all group cursor-pointer shadow-lg hover:shadow-purple-500/30"
            >
              <motion.div 
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
                className="size-12 rounded-full bg-purple-500/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-purple-500/30 transition-all group-hover:scale-110"
              >
                <TrendingUp className="w-6 h-6 text-purple-400 group-hover:text-purple-300 transition-colors" />
              </motion.div>
              <motion.p 
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.55 }}
                className="text-white font-semibold mb-2 group-hover:text-purple-300 transition-colors"
              >
                10% Production Uplift
              </motion.p>
              <motion.p 
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6 }}
                className="text-gray-500 text-sm group-hover:text-gray-400 transition-colors"
              >
                Optimized cycles = more gas to the sales line
              </motion.p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ y: -12, scale: 1.03, transition: { type: 'spring', stiffness: 400, damping: 10 } }}
              viewport={{ once: true }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="bg-surface-dark/50 backdrop-blur-sm rounded-xl p-6 border border-white/5 text-center hover:border-cyan-500/50 transition-all group cursor-pointer shadow-lg hover:shadow-cyan-500/30"
            >
              <motion.div 
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6 }}
                className="size-12 rounded-full bg-cyan-500/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-cyan-500/30 transition-all group-hover:scale-110"
              >
                <Shield className="w-6 h-6 text-cyan-400 group-hover:text-cyan-300 transition-colors" />
              </motion.div>
              <motion.p 
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.65 }}
                className="text-white font-semibold mb-2 group-hover:text-cyan-300 transition-colors"
              >
                70% Faster Recovery
              </motion.p>
              <motion.p 
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.7 }}
                className="text-gray-500 text-sm group-hover:text-gray-400 transition-colors"
              >
                Automated revival from non-arrivals and loading events
              </motion.p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ y: -12, scale: 1.03, transition: { type: 'spring', stiffness: 400, damping: 10 } }}
              viewport={{ once: true }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="bg-surface-dark/50 backdrop-blur-sm rounded-xl p-6 border border-white/5 text-center hover:border-yellow-500/50 transition-all group cursor-pointer shadow-lg hover:shadow-yellow-500/30"
            >
              <motion.div 
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.7 }}
                className="size-12 rounded-full bg-yellow-500/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-yellow-500/30 transition-all group-hover:scale-110"
              >
                <DollarSign className="w-6 h-6 text-yellow-400 group-hover:text-yellow-300 transition-colors" />
              </motion.div>
              <motion.p 
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.75 }}
                className="text-white font-semibold mb-2 group-hover:text-yellow-300 transition-colors"
              >
                Measurable ROI
              </motion.p>
              <motion.p 
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.8 }}
                className="text-gray-500 text-sm group-hover:text-gray-400 transition-colors"
              >
                Payback typically within first quarter of deployment
              </motion.p>
            </motion.div>
          </div>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="text-center mt-12 mb-12"
          >
            <Link to="/schedule-a-demo">
              <Button variant="outline" icon={Calculator} className="mx-auto">
                Calculate Your Potential Impact
              </Button>
            </Link>
          </motion.div>
          {/* Plunger Lift Explanation + Diagram */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left: Explanation */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex flex-col gap-6"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/30 w-fit">
                <Activity className="w-4 h-4 text-primary" />
                <span className="text-xs font-semibold text-primary uppercase tracking-wider">The Optimization Challenge</span>
              </div>
              
              <h4 className="text-2xl md:text-3xl font-bold text-white">
                Why Timing Is Everything
              </h4>
              
              <p className="text-gray-300 leading-relaxed">
                Plunger lift is the most common artificial lift method—but every well behaves differently. 
                <span className="text-primary font-medium">Cycle timing</span> determines whether gas flows to sales 
                or vents to atmosphere. Get it wrong, and you're losing money on every cycle.
              </p>

              <div className="space-y-4 border-l-2 border-primary/30 pl-5">
                <div className="flex items-start gap-3">
                  <div className="size-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-primary">1</span>
                  </div>
                  <p className="text-gray-400 text-sm"><span className="text-white font-medium">Shutin:</span> Too short = weak lift. Too long = lost production time.</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="size-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-primary">2</span>
                  </div>
                  <p className="text-gray-400 text-sm"><span className="text-white font-medium">Arrival:</span> Non-arrivals mean downtime, equipment damage, manual recovery.</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="size-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-primary">3</span>
                  </div>
                  <p className="text-gray-400 text-sm"><span className="text-white font-medium">Afterflow:</span> End too early = gas left in hole. End too late = venting.</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="size-6 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-red-400">!</span>
                  </div>
                  <p className="text-gray-400 text-sm"><span className="text-red-400 font-medium">The Reality:</span> Conditions change hourly. Static timers can't keep up.</p>
                </div>
              </div>
            </motion.div>

            {/* Right: Animated Plunger Diagram */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="glass-panel rounded-2xl p-6 border border-white/10">
                <div className="aspect-square bg-gradient-to-br from-surface-dark to-background-dark rounded-xl flex items-center justify-center relative overflow-hidden max-w-[350px] mx-auto">
                  {/* Animated Plunger Lift SVG */}
                  <svg viewBox="0 0 200 400" className="w-full h-full max-w-[280px]">
                    <defs>
                      <linearGradient id="tubingGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#3a3a3a" />
                        <stop offset="30%" stopColor="#5a5a5a" />
                        <stop offset="70%" stopColor="#5a5a5a" />
                        <stop offset="100%" stopColor="#3a3a3a" />
                      </linearGradient>
                      <linearGradient id="plungerBodyGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#c65d3e" />
                        <stop offset="30%" stopColor="#E87A5D" />
                        <stop offset="70%" stopColor="#E87A5D" />
                        <stop offset="100%" stopColor="#c65d3e" />
                      </linearGradient>
                      <linearGradient id="sealGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#8a4030" />
                        <stop offset="50%" stopColor="#a84d38" />
                        <stop offset="100%" stopColor="#8a4030" />
                      </linearGradient>
                      <linearGradient id="liquidGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="rgba(80,130,180,0.6)" />
                        <stop offset="100%" stopColor="rgba(60,110,160,0.8)" />
                      </linearGradient>
                      <radialGradient id="bubbleGrad">
                        <stop offset="0%" stopColor="rgba(255,255,255,0.4)" />
                        <stop offset="100%" stopColor="rgba(255,255,255,0.05)" />
                      </radialGradient>
                    </defs>

                    {/* Tubing outer walls */}
                    <rect x="55" y="20" width="12" height="360" fill="url(#tubingGrad)" rx="2" />
                    <rect x="133" y="20" width="12" height="360" fill="url(#tubingGrad)" rx="2" />
                    
                    {/* Tubing inner area */}
                    <rect x="67" y="20" width="66" height="360" fill="#1a1a1a" />

                    {/* Gas bubbles - rise with plunger */}
                    <motion.g
                      animate={{ y: [180, 180, 180, 0, 0, 0, 180], opacity: [0.3, 0.5, 0.8, 0.8, 1, 0.5, 0.3] }}
                      transition={{ duration: 8, repeat: Infinity, times: [0, 0.1, 0.4, 0.5, 0.8, 1], ease: "easeInOut" }}
                    >
                      <circle cx="80" cy="60" r="3" fill="url(#bubbleGrad)" />
                      <circle cx="95" cy="50" r="2.5" fill="url(#bubbleGrad)" />
                      <circle cx="115" cy="55" r="2" fill="url(#bubbleGrad)" />
                      <circle cx="105" cy="45" r="3" fill="url(#bubbleGrad)" />
                    </motion.g>

                    {/* Animated Plunger with Liquid on top - synced together */}
                    <motion.g
                      animate={{ y: [0, 0, 180, 180, 0, 0] }}
                      transition={{ duration: 8, repeat: Infinity, times: [0, 0.1, 0.4, 0.5, 0.8, 1], ease: "easeInOut" }}
                    >
                      {/* Liquid load on top of plunger - only visible at bottom and during rise, NOT during fall */}
                      <motion.g
                        animate={{ opacity: [0, 0, 0, 0.9, 0.9, 0] }}
                        transition={{ duration: 8, repeat: Infinity, times: [0, 0.1, 0.4, 0.5, 0.78, 0.8], ease: "easeInOut" }}
                      >
                        <rect x="69" y="48" width="62" height="40" fill="url(#liquidGrad)" />
                        <ellipse cx="100" cy="48" rx="31" ry="4" fill="rgba(100,160,220,0.6)" />
                      </motion.g>
                      <ellipse cx="100" cy="88" rx="24" ry="6" fill="#7a4030" />
                      <rect x="76" y="88" width="48" height="8" fill="url(#plungerBodyGrad)" />
                      <ellipse cx="100" cy="96" rx="24" ry="5" fill="url(#plungerBodyGrad)" />
                      <rect x="82" y="96" width="36" height="12" fill="url(#plungerBodyGrad)" />
                      <rect x="74" y="108" width="52" height="10" rx="2" fill="url(#sealGrad)" />
                      <rect x="82" y="118" width="36" height="10" fill="url(#plungerBodyGrad)" />
                      <rect x="74" y="128" width="52" height="10" rx="2" fill="url(#sealGrad)" />
                      <rect x="82" y="138" width="36" height="10" fill="url(#plungerBodyGrad)" />
                      <rect x="74" y="148" width="52" height="10" rx="2" fill="url(#sealGrad)" />
                      <rect x="82" y="158" width="36" height="12" fill="url(#plungerBodyGrad)" />
                      <path d="M82 170 L100 188 L118 170 Z" fill="url(#plungerBodyGrad)" />
                    </motion.g>

                    {/* Liquid droplets expelled at top */}
                    <motion.g
                      animate={{ opacity: [0, 0, 0, 0, 1, 1, 0], y: [0, 0, 0, 0, 0, -20, -40] }}
                      transition={{ duration: 8, repeat: Infinity, times: [0, 0.5, 0.7, 0.78, 0.8, 0.9, 1], ease: "easeOut" }}
                    >
                      <circle cx="85" cy="25" r="4" fill="rgba(80,140,200,0.7)" />
                      <circle cx="100" cy="20" r="5" fill="rgba(80,140,200,0.8)" />
                      <circle cx="115" cy="25" r="3" fill="rgba(80,140,200,0.6)" />
                    </motion.g>

                    {/* Labels */}
                    <text x="155" y="30" fill="#6a9fd4" fontSize="9" fontFamily="sans-serif">Gas Out</text>
                    <text x="155" y="140" fill="#E87A5D" fontSize="9" fontFamily="sans-serif">Plunger</text>
                    <text x="155" y="280" fill="#6a9fd4" fontSize="9" fontFamily="sans-serif">Liquid Load</text>
                  </svg>
                </div>
                <p className="text-center text-gray-500 text-xs mt-4">Plunger lift cycle</p>
              </div>
            </motion.div>
          </div>

        </div>
      </section>


      {/* Access Anywhere Section */}
      <section className="py-12 sm:py-16 lg:py-24 px-4 sm:px-6 bg-gradient-to-b from-surface-dark to-background-dark relative overflow-hidden">
        {/* Radial glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] lg:w-[800px] h-[600px] lg:h-[800px] bg-primary/10 blur-[100px] lg:blur-[150px] rounded-full pointer-events-none"></div>
        
        <div className="max-w-[1280px] mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Left - Content */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 mb-6"
              >
                <Globe className="w-5 h-5 text-blue-400" />
                <span className="text-sm font-semibold text-blue-400">Oversight & Control</span>
              </motion.div>
              
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4 sm:mb-6"
              >
                You Supervise.{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">
                  The System Operates.
                </span>
              </motion.h2>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="text-base sm:text-lg text-gray-400 mb-6 sm:mb-8 leading-relaxed"
              >
                Full visibility into every well, every cycle—from anywhere. 
                Your team stays informed. The AI stays in control. Manual override available whenever you need it.
              </motion.p>

              {/* Platform Features */}
              <div className="space-y-4">
                {[
                  { icon: Smartphone, label: 'Mobile Visibility', desc: 'Real-time status and alerts on iOS & Android' },
                  { icon: Monitor, label: 'Operations Dashboard', desc: 'Fleet-wide performance at a glance' },
                  { icon: Bell, label: 'Exception Alerts', desc: 'Notified only when human attention is needed' },
                  { icon: Lock, label: 'Manual Override', desc: 'Full control available—the system respects your authority' },
                ].map((platform, i) => (
                  <motion.div
                    key={platform.label}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + i * 0.1 }}
                    className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10 hover:border-blue-500/30 transition-colors group"
                  >
                    <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400 group-hover:bg-blue-500 group-hover:text-white transition-colors">
                      <platform.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">{platform.label}</p>
                      <p className="text-sm text-gray-500">{platform.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right - Device Mockups */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              {/* Mobile Layout - Stack vertically */}
              <div className="lg:hidden flex flex-col gap-6">
                {/* Desktop Monitor - Mobile */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className="bg-surface-dark rounded-xl border border-white/10 shadow-2xl overflow-hidden"
                >
                  <div className="h-5 sm:h-6 bg-black/50 flex items-center px-2 sm:px-3 gap-1 sm:gap-1.5">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-red-500"></div>
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-yellow-500"></div>
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-green-500"></div>
                  </div>
                  <div className="p-1 bg-gradient-to-br from-surface-dark to-black/50">
                    <img 
                      src="/optm.gif" 
                      alt="PlungerXcel Dashboard" 
                      className="w-full h-auto rounded-b-lg"
                    />
                  </div>
                </motion.div>

                {/* Phone - Mobile (portrait) */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 }}
                  className="w-[80%] sm:w-[60%] mx-auto bg-surface-dark rounded-3xl border-4 border-gray-700 shadow-2xl overflow-hidden max-w-[340px]"
                  style={{ aspectRatio: '9 / 19' }}
                >
                  <div className="p-1 bg-gradient-to-br from-surface-dark to-black/50 h-full">
                    <img
                      src="/mob-card.gif"
                      alt="PlungerXcel Mobile App"
                      className="w-full h-full object-cover rounded-2xl"
                    />
                  </div>
                </motion.div>
              </div>

              {/* Desktop Layout - Overlapping */}
              <div className="hidden lg:block relative h-[500px]">
                {/* Desktop Monitor */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className="absolute top-0 left-0 w-[95%] xl:w-full bg-surface-dark rounded-xl border border-white/10 shadow-2xl overflow-hidden"
                >
                  <div className="h-6 bg-black/50 flex items-center px-3 gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-red-500"></div>
                    <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                  </div>
                  <div className="p-1 bg-gradient-to-br from-surface-dark to-black/50">
                    <img 
                      src="/optm.gif" 
                      alt="PlungerXcel Dashboard" 
                      className="w-full h-auto rounded-b-lg"
                    />
                  </div>
                </motion.div>

                {/* Phone (desktop composition) - enforce portrait aspect */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9, y: 20 }}
                  whileInView={{ opacity: 1, scale: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.6 }}
                  className="absolute bottom-0 right-0 w-[32%] xl:w-[35%] bg-surface-dark rounded-3xl border-4 border-gray-700 shadow-2xl overflow-hidden"
                  style={{ aspectRatio: '9 / 19', maxWidth: '360px' }}
                >
                  <div className="p-1 bg-gradient-to-br from-surface-dark to-black/50 h-full">
                    <img
                      src="/mob-card.gif"
                      alt="PlungerXcel Mobile App"
                      className="w-full h-full object-cover rounded-2xl"
                    />
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* About / Our Story Section */}
      
      {/* CTA Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 bg-background-dark">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-[1280px] mx-auto p-6 sm:p-8 rounded-xl sm:rounded-2xl bg-gradient-to-r from-primary/20 via-primary/10 to-transparent border border-primary/30 flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6"
        >
          <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 text-center sm:text-left">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-primary/20 flex items-center justify-center text-primary flex-shrink-0">
              <Play className="w-7 h-7 sm:w-8 sm:h-8" />
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-white">Ready for Autonomous Control?</h3>
              <p className="text-gray-400 text-sm sm:text-base">Request a production assessment for your wells</p>
            </div>
          </div>
          <Link to="/schedule-a-demo">
            <Button size="lg" icon={ArrowRight}>
              Schedule Executive Briefing
            </Button>
          </Link>
        </motion.div>
      </section>
    </div>
  );
};

export default LandingPage;
